import { useQuery } from '@tanstack/react-query';
import { kindOfType, readAttachmentContent, type AttachmentItem } from '@evogard/ui';
import type { ClaimDocument } from '@evogard/contract';
import { formatInstant } from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';

/**
 * O que o aviso traz — foto, vídeo ou qualquer arquivo que a casa aceita —,
 * do lado da tela. Formulário, ficha e mesa leem e enviam por aqui; os
 * componentes são os da casa (`Attachments.tsx`), e nenhum desenho de anexo é
 * do sinistro.
 */

/**
 * A URL assinada de um arquivo. Vale cinco minutos e é pedida no clique (ou
 * quando o ladrilho vai desenhar a prévia), nunca ao montar a tela.
 */
async function signedUrl(claimId: number, documentId: number): Promise<string> {
  const r = await api.claims.documentUrl({ params: { id: claimId, documentId } });
  return ok(r, 200, 'Não foi possível abrir o arquivo.').url;
}

/** Um arquivo gravado, no formato da grade da casa. */
export function noticeAttachmentItem(claimId: number, doc: ClaimDocument): AttachmentItem {
  return {
    key: String(doc.id),
    nome: doc.fileName,
    typeMime: doc.mimeType,
    sizeBytes: doc.sizeBytes,
    detail: [doc.createdByName, formatInstant(doc.createdAt)].filter(Boolean).join(' · '),
    resolveUrl: () => signedUrl(claimId, doc.id),
  };
}

/**
 * Sobe os arquivos escolhidos, um de cada vez, e tira da lista cada um que
 * subiu — o mesmo laço do compositor das demandas. Em paralelo, três vídeos
 * disputariam a mesma subida numa rede móvel; serial, o que falhou continua na
 * lista, exatamente para o próximo clique.
 *
 * O base64 é lido aqui, e não na escolha: um vídeo de 15 MB vira ~20 MB de
 * texto na memória da aba, e a prévia não precisa dele.
 */
export async function uploadNoticeFiles(
  claimId: number,
  picked: { items: readonly { key: string; nome: string; typeMime: string; file: File }[] },
  remove: (key: string) => void,
): Promise<void> {
  for (const item of [...picked.items]) {
    const content = await readAttachmentContent(item.file);
    const r = await api.claims.attachNoticeDocument({
      params: { id: claimId },
      body: { fileName: item.nome, mimeType: item.typeMime, content },
    });
    ok(r, 201, `Não foi possível enviar ${item.nome}.`);
    remove(item.key);
  }
}

/** Arquiva um arquivo do aviso. A linha e o arquivo ficam; some da ficha e da mesa. */
export async function archiveNoticeFile(claimId: number, documentId: number): Promise<void> {
  const r = await api.claims.archiveNoticeDocument({
    params: { id: claimId, documentId },
    body: {},
  });
  ok(r, 200, 'Não foi possível tirar o arquivo do aviso.');
}

/**
 * As fotos do aviso com a URL já assinada — para o `ThumbStrip` e o
 * `PhotoViewer` da mesa, que desenham `<img src>` e não sabem pedir assinatura.
 *
 * Uma consulta só, e só das imagens: vídeo e documento ficam na galeria da
 * casa, que assina no clique. A validade da URL é de cinco minutos, então a
 * consulta envelhece antes dela (`staleTime` de quatro) e assina de novo quando
 * a mesa volta a ser vista.
 */
export function useNoticePhotos(claimId: number, docs: readonly ClaimDocument[]) {
  const images = docs.filter((d) => kindOfType(d.mimeType) === 'image');
  return useQuery({
    queryKey: ['sinistros', 'sinistro', claimId, 'fotos', images.map((d) => d.id)],
    enabled: images.length > 0,
    staleTime: 4 * 60_000,
    queryFn: async () =>
      Promise.all(
        images.map(async (d) => ({ src: await signedUrl(claimId, d.id), alt: d.fileName })),
      ),
  });
}
